import { Box, Dialog, IconButton, Typography, Table, TableBody, TableCell, TableFooter, TablePagination, TableRow, CircularProgress, Stack, Button, } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import moment from "moment";
import { RootState } from "../../../../store/types";
import Spinner from "../../../../components/Spinner";
import { i18n } from "../../../../translate/i18n";
import EnhancedTableHead from "../../../../UI/MaterialTableOverrides/EnhancedTableHead";
import TablePaginationActionsWrapped from "../../../../UI/MaterialTableOverrides/TablePaginationActions";
import * as actions from '../../../../store/actions';
import confirm from "../../../../UI/Confirm/confirm";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  loadDraft: (id: string) => Promise<void>;
  deleteDraft: (id: string) => void;
}

const styles = {
  formTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #e0e0e0',
  },
  container: {
    padding: '10px',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  print: {
    marginRight: 5,
  },
  contentContainer: {
    margin: '30px 0',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  opacityContainer: {
    position: 'relative' as 'relative',
    padding: '20px',
  },
  table: {
    position: 'relative',
  },
  tableRow: {
    cursor: 'pointer',
  },
  spinner: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    top: '-5px',
    opacity: '1',
    zIndex: 2,
  },
  noResults: {
    height: '100px',
  },
  deleteTextConfirm: {
    marginLeft: '-30px',
    fontWeight: 'bold',
  },
  editWidth: {
    width: '90%'
  },
  thumbUp: {
    marginRight: 15,
    color: "#349c34"
  },
  thumbDown: {
    marginRight: 15,
    color: "#d04545"
  },
  avatar: {
    width: '2.75rem',
    height: '2.75rem',
    margin: '2px 0.25rem 2px 0.25rem',
  },
  avatarContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  companyName: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '0.75rem',
  },
  avatarSmallText: {
    fontSize: '0.75rem',
    width: '2.75rem',
    height: '2.75rem',
    margin: '2px 0.25rem 2px 0.25rem',
  },
  chip: {
    margin: 5,
  },
  statusContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  cell: {
    fontSize: '0.775rem',
    padding: '0.25rem 2',
  },
}

const ModalServiceDrafts: React.FC<Props> = ({ isOpen, onClose, loadDraft, deleteDraft }) => {

  const dispatch = useDispatch();


  const serviceDrafts = useSelector((state: RootState) => state.createService.serviceDrafts);
  const serviceDraftsLoading = useSelector((state: RootState) => state.createService.serviceDraftsLoading);
  const serviceDraftsCount = useSelector((state: RootState) => state.createService.serviceDraftsCount);
  const loadingDelete = useSelector((state: RootState) => state.createService.deleteServiceDraftLoading);
  const equipmentId = useSelector((state: RootState) => state.createService.selectedEquipmentId);
  const equipmentServiceId = useSelector((state: RootState) => state.createService.selectedEquipmentService._id);
  const deleteDraftId = useSelector((state: RootState) => state.createService.deleteServideDraftId);

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);

  const handleChangePage = (element: any, page: number) => {
    setPage(page + 1);
  };

  const handleChangeRowsPerPage = (element: any) => {
    setLimit(element?.target?.value);
    setPage(1);
  };

  useEffect(() => {
    dispatch(actions.createServicefetchServiceDrafts(page, limit, equipmentId, equipmentServiceId));
  }, [page, limit])

  const headRows = [
    { id: 'createAt', numeric: false, disablePadding: false, label: i18n.t('tableServiceDrafts.createdAt') as string },
    { id: 'lastUpatedAt', numeric: false, disablePadding: false, label: i18n.t('tableServiceDrafts.lastModifiedAt') as string },
    { id: 'service', numeric: false, disablePadding: false, label: i18n.t('tableServiceDrafts.service') as string },
    { id: 'actions', numeric: false, disablePadding: true, label: '' as string },
  ];

  const handleDeleteServiceDraft = async (draftId: string) => {
    confirm(i18n.t('tableServiceDrafts.deleteDraftMessage'),
      {
        title: i18n.t('tableServiceDrafts.deleteDraftTitle'),
        titleColor: 'red',
        disableBackdrop: true
      }
    ).then(
      () => {},
      () => deleteDraft(draftId),
    )
  }

  return (
    <Dialog
      maxWidth='lg'
      fullWidth
      scroll='body'
      open={isOpen}
      onClose={() => onClose()}
    >
      <Box sx={{ ...styles.opacityContainer, opacity: '1' }}>
        <Box sx={styles.formTitle}>
          <Typography variant='h6'>{i18n.t('tableServiceDrafts.pageTitle') as string}</Typography>
          <IconButton onClick={() => onClose()} size="large"><CloseIcon /></IconButton>
        </Box>
        <Table sx={styles.table}>
          <tbody>
            <tr>
              <td>
                {serviceDraftsLoading &&
                  <Box sx={styles.spinner}>
                    <Spinner overlay />
                  </Box>}
                {serviceDrafts.length === 0 &&
                  <div style={styles.noResults}>
                    {!serviceDraftsLoading && <Typography sx={styles.spinner}>{i18n.t('tableTitleCompany.Typography') as string}</Typography>}
                  </div>}
              </td>
            </tr>
          </tbody>
          <EnhancedTableHead
            headRows={headRows}
            order={''}
            orderBy={''}
            onRequestSort={() => {}}
          />
          <TableBody style={styles.opacityContainer}>
            {serviceDrafts.map((draft) => (
              <TableRow
                style={styles.tableRow}
                key={draft._id}
                hover
              >
                <TableCell style={styles.cell}>{moment(draft.createdAt as any).format('DD/MM/YYYY HH:mm')}</TableCell>
                <TableCell style={styles.cell}>{moment(draft.lastModifiedAt ).format('DD/MM/YYYY HH:mm')}</TableCell>
                <TableCell style={styles.cell}>{draft.serviceType.name + ' ' + draft.frequency.name}</TableCell>

                <TableCell style={styles.cell}>
                  <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={async () => {await loadDraft(draft._id); onClose();}}
                      disabled={loadingDelete}
                    >
                      {i18n.t('tableServiceDrafts.finishService') as string}
                    </Button>
                    <IconButton
                      size="small"
                      color="inherit"
                      onClick={() => handleDeleteServiceDraft(draft._id)}
                      disabled={loadingDelete}
                    >
                      { (loadingDelete && deleteDraftId === draft._id) ? <CircularProgress size={14} /> : <DeleteIcon fontSize="small" /> }
                    </IconButton>
                  </Stack>
                </TableCell>
                
              </TableRow>
            ))}
          </TableBody>
          <TableFooter style={styles.opacityContainer}>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                count={serviceDraftsCount}
                rowsPerPage={limit}
                page={page - 1}
                SelectProps={{
                  native: true
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActionsWrapped}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </Box>
    </Dialog>
  );
};

export default ModalServiceDrafts;
